import React from 'react';
import { motion } from 'framer-motion';
import { ExternalLink, Calendar, Clock } from 'lucide-react';
import { SiMedium } from 'react-icons/si';
import { blogs } from '../data/mock';
import { Card } from './ui/card';
import { Badge } from './ui/badge';

const Blogs = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };

  return (
    <section id="blogs" className="section-padding bg-[#0f0f0f]">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="section-title">Engineering Insights</h2>
          <p className="section-subtitle mx-auto">
            Sharing knowledge on mobile architecture, performance, and scalability
          </p>
        </motion.div>

        {/* Blogs Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {blogs.map((blog) => (
            <motion.div
              key={blog.id}
              variants={cardVariants}
              whileHover={{ y: -8 }}
            >
              <Card
                className="bg-[#1a1a1a] border-[#2a2a2a] hover:border-[#404040] p-6 cursor-pointer transition-all duration-300 h-full flex flex-col"
                onClick={() => window.open(blog.mediumUrl, '_blank')}
              >
                {/* Medium Badge */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2 px-3 py-1 bg-[#2a2a2a] rounded-full">
                    <SiMedium size={14} className="text-white" />
                    <span className="text-xs text-gray-400 monospace">Medium</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <Clock size={14} />
                    <span>{blog.readTime} read</span>
                  </div>
                </div>

                {/* Title */}
                <h3 className="text-xl font-bold mb-3 text-white hover:text-gray-200 transition-colors line-clamp-2">
                  {blog.title}
                </h3>

                {/* Description */}
                <p className="text-sm text-gray-400 mb-4 leading-relaxed line-clamp-3 flex-grow">
                  {blog.description}
                </p>

                {/* Tags */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {blog.tags.map((tag) => (
                    <Badge key={tag} className="bg-[#2a2a2a] text-gray-400 text-xs border-0">
                      {tag}
                    </Badge>
                  ))}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between pt-4 border-t border-[#2a2a2a]">
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <Calendar size={14} />
                    <span>
                      {new Date(blog.publishDate).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors">
                    <span className="font-medium">Read More</span>
                    <ExternalLink size={14} />
                  </div>
                </div>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Blogs;